/**
 * Directory walker for the polling fallback.
 *
 * Returns paths of files modified within the last `sinceMs` milliseconds,
 * excluding well-known ignore directories and non-code extensions. Used
 * by `watch-live.ts` to decide whether to run a drift check on this poll
 * cycle.
 *
 * Kept separate so it can be tested in isolation without spinning up the
 * full watcher, and so the watcher's hot path stays focused on trigger /
 * verifier / stop-rule orchestration.
 */
import { lstatSync, readdirSync } from "node:fs";
import { join, relative } from "node:path";
import { WATCH_IGNORE_DIRS, WATCH_IGNORE_EXTENSIONS } from "./watch-ignore.js";

/** Default depth cap to bound the walk on deep monorepos. */
const DEFAULT_MAX_DEPTH = 10;

export interface RecentlyChangedOptions {
  /** Cap recursion depth (default 10). Symlinks are not followed, so this
   *  bound applies to actual directory levels in the tree. */
  maxDepth?: number;
}

export function recentlyChangedFiles(
  projectRoot: string,
  sinceMs: number,
  opts: RecentlyChangedOptions = {}
): string[] {
  const maxDepth = opts.maxDepth ?? DEFAULT_MAX_DEPTH;
  const out: string[] = [];
  const cutoff = Date.now() - sinceMs;
  // Each stack entry carries its depth so we can enforce the cap uniformly.
  const stack: Array<{ dir: string; depth: number }> = [
    { dir: projectRoot, depth: 0 },
  ];

  while (stack.length) {
    const { dir, depth } = stack.pop()!;
    let entries: string[];
    try {
      entries = readdirSync(dir);
    } catch {
      continue;
    }
    for (const name of entries) {
      if (WATCH_IGNORE_DIRS.has(name)) continue;
      // Skip files whose basename matches an ignored extension (.log,
      // .tmp, .pid, ...). We compare the basename, not the full path,
      // so a directory named "lock" is unaffected but a file named
      // "build.log" is filtered.
      const dot = name.lastIndexOf(".");
      if (dot > 0 && WATCH_IGNORE_EXTENSIONS.has(name.slice(dot))) continue;

      const full = join(dir, name);
      let stat: import("node:fs").Stats;
      try {
        // lstat, not stat — symlinks report as a symlink rather than
        // following the target. We never descend into symlinked dirs to
        // prevent cycles (e.g. ./node_modules -> ./packages/foo/node_modules).
        stat = lstatSync(full);
      } catch {
        continue;
      }

      if (stat.isSymbolicLink()) {
        // Don't follow symlinks. A change inside the target would be
        // observed if the target is also a watch path, but a symlink
        // loop or a deeply-nested symlinked tree cannot trap the walk.
        continue;
      }
      if (stat.isDirectory()) {
        if (depth + 1 < maxDepth) {
          stack.push({ dir: full, depth: depth + 1 });
        }
        continue;
      }
      if (stat.isFile() && stat.mtimeMs >= cutoff) {
        out.push(relative(projectRoot, full));
      }
    }
  }
  return out;
}